import React, {useEffect, useState, useRef, ChangeEvent} from "react";
import {Link, useNavigate} from "react-router-dom";
import InterfaceSection from "../../interface/InterfaceSection";
import InterfaceRegion from "../../interface/InterfaceRegion";
import {
  SVVButtonRow,
  SVVButton,  
  SVVButtonCustom, 
  SVVButtonCancel,
} from "@komponentkassen/svv-button";
import {SVVInformationMessage} from "@komponentkassen/svv-information-message";
import {SVVSelectOne, SVVSelect} from "@komponentkassen/svv-form";
import "@komponentkassen/svv-information-message-css/dist/main.css";
import PillButton from "../../components/PillButton";
import InterfaceService from "../../interface/InterfaceService";
import {SVVSpinner} from "@komponentkassen/svv-spinner";
import api from "../../helpers/api";
import {useApptekster} from "../../apptekst-context";
import FormButtons from "../../components/FormButtons";
import {SVVHeading} from "@komponentkassen/svv-text";

type ViewModes = "Nearest" | "All" | "None";

const Section = (props: {
  section: InterfaceSection;
  setSection: any;
  setService?: (service: InterfaceService) => void,
  service: InterfaceService;
  setNavState: any;
  abort: any;
  nextUrl: string,
  prevUrl?: string,
  resetBooking: () => void
  forrigeNavStateVerdi?: number
  avbrytUrl?: string
}) => {
  const t = useApptekster();
  const navigate = useNavigate();
  const [showNoTrafficStationsValidationMessage, setShowNoTrafficStationsValidationMessage] = useState<boolean>(false);
  const [showNearestValidationMessage, setShowNearestValidationMessage] = useState<boolean>(false);
  const [showNoCountyValidationMessage, setShowNoCountyValidationMessage] = useState<boolean>(false);
  const [showNoSelectedTrafficStationValidationMessage, setShowNoSelectedTrafficStationValidationMessage] = useState<boolean>(false);
  const [sections, setSections] = useState<InterfaceSection[]>([]);
  const [regions, setRegions] = useState<InterfaceRegion[]>([]);
  const [regionId, setRegionId] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewModes>("All");
  const [showSelected, setShowSelected] = useState(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  
  const {section, setSection} = props;
  
  useEffect(() => {
    if (section.sectionId !== 0 && section.sectionName !== "") {
      setShowSelected(true);
    } else {
      getAllRegions()
    }
    //Disable eslint fordi denne effecten skal kun kjøres én gang når komponenten mountes
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (headingRef?.current) {
      headingRef.current.focus()
    }
  }, [headingRef])

  function getSectionsByLocation(latitude: number, longitude: number) {
    api
      .get(
        `/api/Timebestilling/getclosest?latitude=${latitude}&longitude=${longitude}&take=5`
      )
      .then(response => {
        setSections(response.data);
      })
      .catch(function (error) {
        // handle error
        console.log(error);
      });
  }

  function getSections(sectionId: string) {
    const serviceId: number = props?.service?.serviceId;
    api
      .get(
        `/api/Timebestilling/getsectionswithservice/${serviceId}/${sectionId}`
      )
      .then((response) => {
        setSections(response.data);
      })
      .catch((error) => {
        // handle error
        console.log(error);
      });
  }

  const showNearestClick = (e: any) => {
    e.preventDefault()
    setViewMode("Nearest");
    if (sections) {
      setSections([]);
    }
    setShowNoTrafficStationsValidationMessage(false);
    setShowNoSelectedTrafficStationValidationMessage(false);
    getLocation();
  }

  const showAllClick = (e: any) => {
    e.preventDefault()
    setViewMode("All");
    setSections([]);
    setShowNearestValidationMessage(false);
    setShowNoCountyValidationMessage(false);
    setShowNoTrafficStationsValidationMessage(false);
    setShowNoSelectedTrafficStationValidationMessage(false);
    if (regionId) {
      getSections(regionId);
    } else {
      getAllRegions();
    }
  }

  function getLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        positionSuccess,
        () => {
          setShowNearestValidationMessage(true);
        });
    } else {
      alert("Error in geolocation");
      // FIX ME to show in error area
      //x.innerHTML = "Geolocation is not supported by this browser.";
    }
  }

  const positionSuccess = (position: any) => {
    setShowNearestValidationMessage(false);
    getSectionsByLocation(position.coords.latitude, position.coords.longitude);
  }

  function getAllRegions() {
    api
      .get(`/api/Timebestilling/getregions`)
      .then(function (response) {
        setRegions(response.data);
      })
      .catch(function (error) {
        // handle error
        console.log(error);
      });
  }

  const submit = (event: any) => {
    event.preventDefault();

    if (section.sectionId && section.sectionName) {
      props.setNavState();
      navigate(props.nextUrl);
    } else {
      if (viewMode === "All") {
        if (!regionId) {
          setShowNoCountyValidationMessage(true);
        }
        if (regionId && !section.sectionId) {
          setShowNoSelectedTrafficStationValidationMessage(true);
        }
      }
      if (viewMode === "Nearest" && !showNearestValidationMessage) {
        if (!section.sectionId) {
          setShowNoSelectedTrafficStationValidationMessage(true);
        }
      }
      if (viewMode === "None") {
        if (!sections || sections?.length === 0) {
          setShowNoTrafficStationsValidationMessage(true);
        }
      }
    }
  }

  const selectSectionChangeHandler = (id: string) => {
    const selected = sections.find(section => section.sectionId === parseInt(id));
    setShowNoSelectedTrafficStationValidationMessage(false);
    setSection({
      sectionId: selected ? selected.sectionId : 0,
      sectionName: selected ? selected.sectionName : ''
    })
  }

  const selectCountyChangeHandler = (countyId: string) => {
    setRegionId(countyId);
    setShowNoSelectedTrafficStationValidationMessage(false);
    props.setSection({sectionId: 0, sectionName: ''});
    if (countyId) {
      getSections(countyId);
    } else {
      setSections([]);
    }
    setShowNoCountyValidationMessage(false);
  }

  const removeSelectedSection = () => {
      setShowSelected(false);
      getAllRegions()
      props.resetBooking()
  }

  const showSpinnerForRegions: boolean = (regions.length === 0);

  const showSpinnerForSections: boolean = !!((viewMode === "Nearest" || (viewMode === "All" && regionId)) &&
      sections?.length === 0 && !showNearestValidationMessage);

  const showSelectOne: boolean =  !!((viewMode === "Nearest" || (viewMode === "All" && regionId)) &&
     sections?.length > 0);

  return (
    <>
      <SVVHeading
        level="h2"
        ref={headingRef}
        tabIndex={-1}
      >
        {t("timebestilling.velg-trafikkstasjon")}
      </SVVHeading>

      {showSelected &&
        <>
          <p className="svv-label spacing-bottom--xxsmall">
            {t("timebestilling.valgt-trafikkstasjon")}
          </p>
          <div className="spacing-bottom--small">
            <PillButton
              value={section.sectionName}
              onClick={removeSelectedSection}
              ariaLabel={t("timebestilling.fjern-valgt-trafikkstasjon")}
            />
          </div>
          <SVVButtonRow isFormRow>
            <SVVButtonCustom
              asElement={Link}
              href={props.nextUrl}
              hrefAttribute="to"
            >
              {t("timebestilling.neste")}
            </SVVButtonCustom>
            {props.prevUrl &&
              <SVVButtonCustom
                asElement={Link}
                href={props.prevUrl}
                hrefAttribute="to"
                color="secondary"
              >
                {t("timebestilling.tilbake")}
              </SVVButtonCustom>
            }
            <SVVButtonCancel
              asElement={Link}
              hrefAttribute="to"
              href="/"
              onClick={props.abort}
            >
              {t("timebestilling.avbryt")}
            </SVVButtonCancel>
          </SVVButtonRow>
        </>
      }

      {!showSelected &&
        <>
          <form onSubmit={submit} noValidate>
          <SVVButtonRow>
            <SVVButton 
              onClick={showNearestClick}
              color="secondary"
              aria-describedby={"lokasjon-feilmelding"}
              small
            >
              {t("timebestilling.narmeste")}
            </SVVButton>
            <SVVButton 
              onClick={showAllClick}
              color="secondary"
              small
            >
              {t("timebestilling.vis-alle")}
            </SVVButton>
          </SVVButtonRow>
            <div className="spacing-top--medium">
              {viewMode === "All" && regions && (
                <>
                  {showSpinnerForRegions && <SVVSpinner>{t("timebestilling.henter-fylker")}</SVVSpinner>}
                  {regions.length > 0 &&
                    <SVVSelect
                      selected={regionId}
                      onChange={(e:ChangeEvent<HTMLSelectElement>) => selectCountyChangeHandler(e.target.value)}
                      id="Region-select"
                      legend={t("timebestilling.velg-fylke")}
                      options={
                        [...regions.map((region) => ({
                          value: region.regionId.toString(),
                          text: region.regionName,
                        })),
                        ]
                      }
                      errorMessage={showNoCountyValidationMessage ? t("timebestilling.du-ma-velge-fylke") : ""}
                      emptyChoiceText={t("timebestilling.velg")}
                      required
                    />
                  }
                </>
              )}

              {showSpinnerForSections && <SVVSpinner>{t("timebestilling.hent-trafikkstasjoner")}</SVVSpinner>}
              {showSelectOne && 
                <SVVSelectOne
                  onChange={(event: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) => selectSectionChangeHandler(event.target.value)}
                  selected={section.sectionId.toString(10)}
                  id="Section-select"
                  legend={t("timebestilling.velg-trafikkstasjon")}
                  name={t("timebestilling.velg-trafikkstasjon")}
                  options={sections.map((section) => ({
                    value: section.sectionId.toString(),
                    text: section.sectionName
                  }))}
                  errorMessage={showNoSelectedTrafficStationValidationMessage ?
                        t("timebestilling.du-ma-velge-trafikkstasjon") :
                        ""}
                  emptyChoiceText="Velg"
                  direction="vertical"
                  required
                />
              }
            </div>

            <div className="abstract" aria-live="assertive">
              {showNoTrafficStationsValidationMessage &&
                <SVVInformationMessage type="error">
                  <p>
                    {t("timebestilling.velg-trafikkstasjon-finn-stasjoner")}
                  </p>
                </SVVInformationMessage>
              }
              <div id="lokasjon-feilmelding">
                {showNearestValidationMessage &&
                  <SVVInformationMessage type="error">
                    <p>
                      {t("timebestilling.godta-deling")}
                    </p>
                  </SVVInformationMessage>
                }
              </div>
            </div>
            <FormButtons 
              submitText={t("timebestilling.neste")} 
              backText={t("timebestilling.tilbake")}
              cancelText={t("timebestilling.avbryt")}
              onBackClick={() => props.setNavState(props.forrigeNavStateVerdi)}
              backUrl={props.prevUrl || undefined}
              onCancel={props.abort}
              avbrytUrl={props.avbrytUrl}
            />
          </form>
        </>
      }
    </>
  );
};

export default Section;
